import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "motion/react";
import { Mail, Send, ArrowLeft, HelpCircle, AlertCircle } from "lucide-react";
import { useToast } from "../../../components/feedback/ToastProvider";
import logoImg from "../../../assets/logo.png";
import { useAuth0 } from "@auth0/auth0-react";
import "./EmailUnverifiedPage.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const AUTH_CONNECTION_KEY = "legup_auth_connection";

export default function EmailUnverifiedPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showToast } = useToast();
  const { loginWithRedirect } = useAuth0();

  const email = searchParams.get("email") || "your email address";

  const connectionFromQuery = searchParams.get("connection") || "";

  const storedConnection =
    window.sessionStorage.getItem(AUTH_CONNECTION_KEY) || "";

  const authConnection = connectionFromQuery || storedConnection;

  async function handleResendVerification() {
    try {
      const res = await fetch(
        `${API_BASE}/api/auth/resend-verification-email/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email }),
        },
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data.detail || "Could not resend verification email.");
      }

      showToast({
        type: "success",
        title: "Verification email sent",
        message: "Please check your inbox or spam folder.",
        duration: 9000,
      });
    } catch (error) {
      showToast({
        type: "error",
        title: "Could not resend email",
        message: error.message || "Please try again in a few moments.",
        duration: 9000,
      });
    }
  }

  function goToLogin() {
    if (authConnection) {
      window.sessionStorage.setItem(AUTH_CONNECTION_KEY, authConnection);
    }

    loginWithRedirect({
      appState: {
        returnTo: "/post-login",
      },

      authorizationParams: {
        prompt: "login",

        ...(authConnection
          ? {
              connection: authConnection,
            }
          : {}),

        ...(email !== "your email address"
          ? {
              login_hint: email,
            }
          : {}),
      },
    });
  }

  return (
    <div className="email-unverified-page">
      <div className="email-unverified-grid" />

      <motion.div
        className="email-unverified-logo"
        initial={{ opacity: 0, x: -18 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
        onClick={() => navigate("/")}
      >
        <img src={logoImg} alt="LEG-UP" />
      </motion.div>

      <motion.main
        className="email-unverified-card"
        initial={{ opacity: 0, y: 28, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      >
        <div className="email-unverified-visual">
          <motion.div
            className="email-unverified-envelope"
            animate={{ y: [0, -6, 0] }}
            transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut" }}
          >
            <motion.span
              className="email-unverified-envelope__pulse"
              animate={{ scale: [0.85, 1.18], opacity: [0.25, 0] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: "easeOut" }}
            />

            <Mail size={58} />

            <motion.span
              className="email-unverified-alert"
              animate={{ scale: [1, 1.08, 1] }}
              transition={{ duration: 1.4, repeat: Infinity }}
            >
              <AlertCircle size={22} />
            </motion.span>
          </motion.div>
        </div>

        <motion.h1
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.18, duration: 0.45 }}
        >
          Verify your email
        </motion.h1>

        <motion.p
          className="email-unverified-text"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.26, duration: 0.45 }}
        >
          Your email address has not been verified yet. Please check your inbox
          and click the verification link we sent to:
        </motion.p>

        <motion.div
          className="email-unverified-email"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.34, duration: 0.45 }}
        >
          <Mail size={22} />
          <strong>{email}</strong>
        </motion.div>

        <div className="email-unverified-divider">
          <span />
          <p>Don’t see the email?</p>
          <span />
        </div>

        <p className="email-unverified-help">
          Check your spam or promotions folder. If you still cannot find it, you
          can resend the verification email.
        </p>

        <div className="email-unverified-actions">
          <button type="button" onClick={handleResendVerification}>
            <Send size={17} />
            Resend verification email
          </button>

          <button type="button" onClick={goToLogin}>
            <ArrowLeft size={17} />
            Back to login
          </button>
        </div>

        <div className="email-unverified-support">
          <HelpCircle size={17} />
          <span>
            Need help? <button type="button">Contact support</button>
          </span>
        </div>
      </motion.main>
    </div>
  );
}
